import React, { useContext, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  CircularProgress,
  Box
} from "@mui/material";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useDropzone } from 'react-dropzone';
import { styled } from '@mui/material/styles';
import { AppContext } from "../context/AppProvider";
import { AuthContext } from "../context/AuthProvider";
import { fetchWithInterceptor } from "../utils/fetchWithInterceptor";

const DropArea = styled('div')(({ theme }) => ({
  border: '2px dashed #aaa',
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(4),
  textAlign: 'center',
  cursor: 'pointer',
  backgroundColor: theme.palette.background.default,
}));

export default function UploadProductPricesDialog({ open, onClose }) {
  const [liquorConnectFile, setLiquorConnectFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [successModalInfo, setSuccessModalInfo] = useState({
    visible: false,
    invalidRows: [],
    parsedRows: null
  });

  const context = useContext(AppContext);
  const authContext = useContext(AuthContext);

  const handleChangeFile = (files) => {
    setLiquorConnectFile(files[0]);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: acceptedFiles => handleChangeFile(acceptedFiles),
    maxFiles: 1,
    maxSize: 5000000,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.csv']
    }
  });

  const hideSuccessModalInfo = () => setSuccessModalInfo({
    visible: false,
    invalidRows: [],
    parsedRows: null
  });

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const formData  = new FormData();
      formData.append('liquorconnect', liquorConnectFile);
  
      const response = await fetchWithInterceptor('/api/integrations/check-prices', {
        method: 'POST',
        body: formData,
        headers: {
          'x-access-token': authContext.getToken()
        },
      });
      setLoading(false);
      if (response.ok) {
        onClose(true);
        const data = await response.json();
        setSuccessModalInfo({
          visible: true,
          parsedRows: data.parsedRows,
          invalidRows: data.invalidRows,
        });
      } else {
        const error = await response.json();
        context.showMessage(error?.message || "Invalid file", "error");
      }
    } catch (error) {
      console.log(error);
      context.showMessage("Invalid file", "error");
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog
        disableEscapeKeyDown
        maxWidth="sm"
        aria-labelledby="delete-dialog"
        open={open}
      >
        <DialogTitle id="delete-dialog">Liquor Connect Products</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ width: '100%' }}>
            <Grid size={{ xs: 12 }} >
              <Box sx={{ minHeight: 200 }}>
                <DropArea {...getRootProps()}>
                  <input {...getInputProps()} />
                  <CloudUploadIcon sx={{ fontSize: 40, mb: 1 }} />
                  <Typography  sx={{ textAlign: 'center', fontSize: '1.5rem' }}>
                    Drag and drop a Liquor Connect file here or click
                  </Typography>
                </DropArea>
              </Box>
              <br />
              <Typography variant="subtitle1">
                Upload a CSV file separated by comma ","
              </Typography>
              <Typography variant="subtitle1">
                {`Uploaded file: ${liquorConnectFile ? liquorConnectFile.name : '-'}`}
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={onClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleSubmit} color="primary" variant="contained" disabled={loading} sx={{ width: 100, height: 36.5 }}>
            {loading ? <CircularProgress size={24} color="inherit" /> : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog onClose={hideSuccessModalInfo} aria-labelledby="simple-dialog-title" open={successModalInfo.visible}>
        <DialogTitle>CSV import</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>{`${successModalInfo.parsedRows} products were imported.`}</Typography>
          {successModalInfo.invalidRows.length ? (
            <>
              <Typography gutterBottom>The following rows contain errors:</Typography>
              <ul>
                {successModalInfo.invalidRows.map((row, key) => <li key={key}>{row}</li>)}
              </ul>
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={hideSuccessModalInfo} color="primary" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
